import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AccessDeniedNotice } from '../../../shared/AccessDeniedNotice';
import { RetryableError } from '../../../shared/RetryableError';
import { ConfirmDialog } from '../../job-roles/components/ConfirmDialog';
import { peopleGateway } from '../../people';
import { MappingDialog, type MappingPrefill, type PickerOption } from '../components/MappingDialog';
import { exportErrorMessage, isForbidden } from '../exportErrors';
import { categoryWithSub, formatDate, mappingScope, unmappedLabel, unmappedReasonLabel } from '../exportFormat';
import { CATEGORIES, categoryLabel, type AccountingMapping, type ChartAccount, type MappingReadiness, type Paged } from '../exportTypes';
import { EmployeeGroupsView } from './EmployeeGroupsView';
import { WageGroupGrid } from './WageGroupGrid';
import { accountingGateway } from '../index';
import { useExportCapabilities } from '../useExportCapabilities';
import '../../job-roles/job-roles.css';
import '../accounting-export.css';

type LoadState = { kind: 'loading' } | { kind: 'ready'; data: Paged<AccountingMapping> } | { kind: 'forbidden' } | { kind: 'error'; message: string };

const PRECEDENCE = [
  'An employee-specific override.',
  'A payroll-code mapping (for example one tax type).',
  'A department or location mapping (department first).',
  'The company default for the category.',
];

const VIEWS = [
  { key: 'mappings', label: 'Mappings' },
  { key: 'by-group', label: 'Wages by group' },
  { key: 'employee-groups', label: 'Employee groups' },
] as const;
type ViewKey = (typeof VIEWS)[number]['key'];
const isView = (v: string | null): v is ViewKey => VIEWS.some((x) => x.key === v);

/** The three views of GL Mapping. They are buttons marked with `aria-current`, not a second tab list, so the page keeps exactly one set of tabs. */
function ViewNav({ view, onChange }: { view: ViewKey; onChange: (v: ViewKey) => void }) {
  return (
    <nav aria-label="GL Mapping views">
      <ul className="ax-subnav">
        {VIEWS.map((v) => (
          <li key={v.key}>
            <button type="button" aria-current={v.key === view ? 'page' : undefined} onClick={() => onChange(v.key)} data-testid={`glm-view-${v.key}`}>{v.label}</button>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/**
 * GL Mapping: the versioned category mappings (the default view), the "By group" wage grid, and the company-defined employee groups. The view lives in the URL
 * (`?view=`), so a link from a preview blocker lands on the right one.
 */
export function GlMappingTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('view');
  const view: ViewKey = isView(requested) ? requested : 'mappings';
  const changeView = (next: ViewKey) => setSearchParams((prev) => {
    const params = new URLSearchParams(prev);
    if (next === 'mappings') params.delete('view'); else params.set('view', next);
    for (const k of ['add', 'category', 'subcategory', 'payrollCode', 'groupId']) params.delete(k);
    return params;
  });
  const nav = <ViewNav view={view} onChange={changeView} />;
  if (view === 'by-group') return <section className="ax-tab" aria-labelledby="ax-gm-title"><h3 id="ax-gm-title">GL Mapping</h3>{nav}<WageGroupGrid /></section>;
  if (view === 'employee-groups') return <section className="ax-tab" aria-labelledby="ax-gm-title"><h3 id="ax-gm-title">GL Mapping</h3>{nav}<EmployeeGroupsView /></section>;
  return <MappingsView nav={nav} />;
}

function MappingsView({ nav }: { nav: ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { caps } = useExportCapabilities();
  const runId = searchParams.get('runId') ?? '';
  const canManage = caps?.canManageMappings !== false;

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [reloadNonce, setReloadNonce] = useState(0);
  const [category, setCategory] = useState('');
  const [showEnded, setShowEnded] = useState(false);
  const [accounts, setAccounts] = useState<ChartAccount[]>([]);
  const [employees, setEmployees] = useState<PickerOption[] | null>(null);
  const [departments, setDepartments] = useState<PickerOption[] | null>(null);
  const [groupNames, setGroupNames] = useState<Map<string, string>>(new Map());
  const [readiness, setReadiness] = useState<MappingReadiness | null>(null);
  const [dialog, setDialog] = useState<{ prefill?: MappingPrefill } | null>(() => (
    searchParams.get('add') === '1'
      ? { prefill: { category: searchParams.get('category') ?? '', subcategory: searchParams.get('subcategory') ?? '', payrollCode: searchParams.get('payrollCode') ?? '' } }
      : null
  ));
  const [ending, setEnding] = useState<AccountingMapping | null>(null);
  const [endBusy, setEndBusy] = useState(false);
  const [endError, setEndError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [runDefaultDate, setRunDefaultDate] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    accountingGateway
      .listMappings({ ...(category ? { category } : {}), ...(showEnded ? {} : { activeOnly: true }), pageSize: 200 }, controller.signal)
      .then((data) => { if (!controller.signal.aborted) setState({ kind: 'ready', data }); })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setState(isForbidden(err) ? { kind: 'forbidden' } : { kind: 'error', message: exportErrorMessage(err, 'Could not load the GL mappings.') });
      });
    return () => controller.abort();
  }, [category, showEnded, reloadNonce]);

  // Accounts for the picker, and the optional employee and department pickers (each is simply not offered when its list is not available to this person).
  useEffect(() => {
    const controller = new AbortController();
    accountingGateway.listChartAccounts({ active: true, pageSize: 500 }, controller.signal)
      .then((page) => { if (!controller.signal.aborted) setAccounts(page.items); })
      .catch(() => { if (!controller.signal.aborted) setAccounts([]); });
    peopleGateway.listEmployees({ pageSize: 100 }, controller.signal)
      .then((page) => { if (!controller.signal.aborted) setEmployees(page.items.map((e) => ({ id: e.id, label: `${e.employeeNumber}: ${e.firstName} ${e.lastName}` }))); })
      .catch(() => { if (!controller.signal.aborted) setEmployees(null); });
    accountingGateway.listEmployeeGroups(controller.signal)
      .then((groups) => { if (!controller.signal.aborted) setGroupNames(new Map(groups.map((g) => [`EmployeeGroup:${g.id}`, g.name]))); })
      .catch(() => undefined);
    peopleGateway.listDepartments()
      .then((rows) => { if (!controller.signal.aborted) setDepartments(rows.filter((d) => d.isActive).map((d) => ({ id: d.id, label: d.name }))); })
      .catch(() => { if (!controller.signal.aborted) setDepartments(null); });
    return () => controller.abort();
  }, []);

  // Arriving from the Payroll Export tab: show what that payroll still needs, and default the effective date to a day that covers it.
  useEffect(() => {
    if (!runId) return;
    const controller = new AbortController();
    accountingGateway.getMappingReadiness(runId, controller.signal)
      .then((r) => { if (!controller.signal.aborted) setReadiness(r); })
      .catch(() => { if (!controller.signal.aborted) setReadiness(null); });
    accountingGateway.listEligiblePayrolls(undefined, controller.signal)
      .then((l) => { if (!controller.signal.aborted) setRunDefaultDate(l.items.find((p) => p.payrollRunId === runId)?.periodStart ?? ''); })
      .catch(() => undefined);
    return () => controller.abort();
  }, [runId, reloadNonce]);

  const employeeNames = useMemo(() => new Map((employees ?? []).map((o) => [o.id, o.label])), [employees]);
  const departmentNames = useMemo(() => new Map((departments ?? []).map((o) => [o.id, o.label])), [departments]);

  const closeDialog = useCallback(() => {
    setDialog(null);
    if (searchParams.get('add')) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const k of ['add', 'category', 'subcategory', 'payrollCode']) next.delete(k);
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  async function endMapping() {
    if (!ending) return;
    setEndBusy(true);
    setEndError(null);
    try {
      await accountingGateway.endMapping(ending.id);
      setNotice(`Ended the mapping for ${categoryWithSub(ending.category, ending.subcategory, ending.payrollCode)}. It is kept as history and exports already generated are unchanged.`);
      setEnding(null);
      setReloadNonce((n) => n + 1);
    } catch (err) {
      setEndError(exportErrorMessage(err, 'The mapping could not be ended.'));
    } finally {
      setEndBusy(false);
    }
  }

  if ((caps && !caps.canPreview) || state.kind === 'forbidden') {
    return (
      <section className="ax-tab" aria-labelledby="ax-gm-title">
        <h3 id="ax-gm-title">GL Mapping</h3>
        <AccessDeniedNotice requiredPermissionLabel="permission to view GL mappings" />
      </section>
    );
  }

  const items = state.kind === 'ready' ? state.data.items : [];

  return (
    <section className="ax-tab" aria-labelledby="ax-gm-title">
      <h3 id="ax-gm-title">GL Mapping</h3>
      {nav}
      <p className="muted ax-lead">
        Tell EPN which general ledger account each kind of payroll amount posts to. A payroll cannot be exported until every item in it has a mapping (or a recorded non-posting rule).
        Mappings are versioned: changing one adds a new version and keeps the old one as history.
      </p>

      <div className="ax-block">
        <h4 id="ax-prec-title">Which mapping applies</h4>
        <ol className="ax-precedence" aria-labelledby="ax-prec-title">
          {PRECEDENCE.map((p) => <li key={p}>{p}</li>)}
        </ol>
        <p className="jr-hint">The most specific mapping wins. When two equally specific mappings both apply, the payroll line is blocked as ambiguous until one is ended.</p>
        <p className="jr-hint" data-testid="wage-precedence-note">
          For gross wages, the export option decides which of these apply: <strong>Single account</strong> uses only the company default (or an earning-code mapping), and <strong>By group</strong> uses an
          employee override, then the mapping for the employee&apos;s group (see Wages by group). Choose it in <Link to="/accounting?tab=export-settings">Export settings</Link> or per export.
        </p>
      </div>

      {runId && (
        <div className="ax-block" data-testid="mapping-context">
          <p><Link to={`/accounting?tab=payroll-export&runId=${encodeURIComponent(runId)}`}>Back to Payroll Export</Link></p>
          {readiness && !readiness.ready && (
            <div className="jr-warning" role="note">
              <p><strong>This payroll still needs mappings for:</strong></p>
              <ul className="jr-plain-list">
                {[...readiness.missing, ...readiness.ambiguous].map((item, i) => (
                  <li key={`${item.category}-${item.subcategory}-${item.payrollCode}-${i}`}>
                    {unmappedLabel(item)}: {unmappedReasonLabel[item.reason] ?? item.reason}{' '}
                    {canManage && (
                      <button type="button" className="ax-link-btn" aria-label={`Add a mapping for ${unmappedLabel(item)}`}
                        onClick={() => setDialog({ prefill: { category: item.category, subcategory: item.subcategory ?? '', payrollCode: item.payrollCode ?? '' } })}>
                        Add mapping
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {readiness?.ready && <p className="jr-ok"><span aria-hidden="true">✔ </span>This payroll has every mapping it needs.</p>}
        </div>
      )}

      {notice && <p className="jr-notice jr-notice--ok" role="status">{notice}</p>}
      {caps && !caps.canManageMappings && <p className="jr-readonly" role="note">You can view GL mappings, but changing them needs a permission you do not have.</p>}

      <div className="ax-toolbar">
        <div className="jr-field">
          <label htmlFor="ax-gm-category">Category</label>
          <select id="ax-gm-category" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">All categories</option>
            {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </div>
        <label className="ax-check">
          <input type="checkbox" checked={showEnded} onChange={(e) => setShowEnded(e.target.checked)} />
          <span>Show ended versions (history)</span>
        </label>
        <span className="ax-toolbar__spacer" />
        {canManage && <button type="button" className="jr-btn jr-btn--primary" onClick={() => setDialog({ prefill: runDefaultDate ? { effectiveFrom: runDefaultDate } : {} })}>Add mapping</button>}
      </div>

      {state.kind === 'loading' && <p role="status" className="muted">Loading GL mappings…</p>}
      {state.kind === 'error' && <RetryableError message={state.message} onRetry={() => { setState({ kind: 'loading' }); setReloadNonce((n) => n + 1); }} />}

      {state.kind === 'ready' && items.length === 0 && (
        <p className="muted" data-testid="no-mappings">No GL mappings yet. Add a mapping for each payroll category you want to export.</p>
      )}

      {state.kind === 'ready' && items.length > 0 && (
        <div className="jr-table-scroll" role="region" aria-label="GL mappings table" tabIndex={0}>
          <table className="jr-table jr-table--actions" data-testid="mappings-table">
            <caption className="ax-caption">GL mappings by category ({state.data.totalCount}){showEnded ? ', including ended versions' : ''}</caption>
            <thead>
              <tr>
                <th scope="col">Category</th>
                <th scope="col">Applies to</th>
                <th scope="col">GL account</th>
                <th scope="col">Effective</th>
                <th scope="col" className="num">Version</th>
                <th scope="col">Status</th>
                {canManage && <th scope="col">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <tr key={m.id}>
                  <th scope="row">{categoryLabel(m.category)}</th>
                  <td>{mappingScope(m, { employees: employeeNames, departments: departmentNames, groups: groupNames })}</td>
                  <td>
                    {m.treatment === 'Post' && m.accountNumber
                      ? <><strong>{m.accountNumber}</strong> {m.accountName} ({m.accountType})<span className="ax-date jr-hint">{m.direction} side</span></>
                      : <>Not posted: {m.nonPostingReason ?? ''}</>}
                  </td>
                  <td><span className="ax-date">From {formatDate(m.effectiveFrom)}</span><span className="ax-date">{m.effectiveTo ? `To ${formatDate(m.effectiveTo)}` : 'No end date'}</span></td>
                  <td className="num">{m.mappingVersion}</td>
                  <td>{m.isActive ? <span className="ax-badge ax-badge--ok"><span aria-hidden="true">✔</span> Active</span> : <span className="ax-badge ax-badge--neutral"><span aria-hidden="true">–</span> Ended</span>}</td>
                  {canManage && (
                    <td>
                      {m.isActive && (
                        <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => { setEnding(m); setEndError(null); }}
                          aria-label={`End the ${categoryWithSub(m.category, m.subcategory, m.payrollCode)} mapping, version ${m.mappingVersion}`}>
                          End mapping
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {state.kind === 'ready' && state.data.totalCount > items.length && (
        <p className="jr-hint">Showing {items.length} of {state.data.totalCount}. Filter by category to see the rest.</p>
      )}
      <p className="jr-hint">A mapping only says where a category posts; it holds no payroll amounts. Ended mappings still show which account an older export used.</p>

      {dialog && (
        <MappingDialog
          prefill={{ ...dialog.prefill, effectiveFrom: dialog.prefill?.effectiveFrom || runDefaultDate }}
          accounts={accounts}
          employees={employees}
          departments={departments}
          onClose={closeDialog}
          onSaved={(saved) => {
            closeDialog();
            setNotice(`Saved a new mapping for ${categoryWithSub(saved.category, saved.subcategory, saved.payrollCode)} (version ${saved.mappingVersion}).`);
            setReloadNonce((n) => n + 1);
          }}
        />
      )}
      {ending && (
        <ConfirmDialog
          title="End this mapping?"
          confirmLabel="End mapping"
          onConfirm={() => void endMapping()}
          onCancel={() => setEnding(null)}
          busy={endBusy}
          error={endError}
        >
          <p>
            The {categoryWithSub(ending.category, ending.subcategory, ending.payrollCode)} mapping to {ending.accountNumber ?? 'no account'} will stop applying to new previews. It is kept as history,
            and exports that were already generated do not change. Payrolls that relied on it will need another mapping.
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}
